import { FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { useAuctionSecondChanceInfo, useAuctionState } from 'dibs-auctions/exports/hooks';

// components
import { Button } from 'dibs-elements/src/Button';
import { PriceDisplay } from 'dibs-buyer-price';
import { FormattedMessage } from 'dibs-react-intl';
import { PdpSharedAuctionViewHistory } from '../PdpSharedAuctionViewHistory/PdpSharedAuctionViewHistory';

// types
import { PdpSharedAuctionClosed_item$key as Item } from './__generated__/PdpSharedAuctionClosed_item.graphql';

// styles
import dibsCss from 'dibs-css';
import styles from './PdpSharedAuctionClosed.scss';
import variantStyles from '../PdpSharedAuctionActions/PdpSharedAuctionActionsTestVariant.scss';

type Props = {
    item: Item;
    currency: string;
    linkToBidHistory?: boolean;
    isAuctionSiderailRedesignVariant?: boolean | null;
    handleRoundPriceTracking?: () => void;
};

const copy = {
    biddingClosed: (
        <FormattedMessage
            id="PdpSharedAuctionClosed.biddingClosed"
            defaultMessage="Bidding Closed"
        />
    ),
};

const itemFragment = graphql`
    fragment PdpSharedAuctionClosed_item on Item {
        serviceId
        relevantAuctionSolution(page: pdp) {
            reserveMet
        }
        isUnavailable
        ...useAuctionState_item @arguments(page: pdp)
        ...useAuctionSecondChanceInfo_item @arguments(page: pdp)
        ...PriceDisplay_item
            @arguments(
                userId: $userId
                isTrade: $isTrade
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
            )
    }
`;

export const PdpSharedAuctionClosed: FC<Props> = ({
    item: itemRef,
    currency,
    linkToBidHistory,
    isAuctionSiderailRedesignVariant,
    handleRoundPriceTracking,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const { isMobile } = useServerVarsContext();
    const { isPausedAuction, isUnavailableAuction } = useAuctionState({
        item,
    });
    const { isSecondChanceOfferSent } = useAuctionSecondChanceInfo({ item });
    const { isUnavailable } = item;
    const { reserveMet } = item?.relevantAuctionSolution || {};

    if (isAuctionSiderailRedesignVariant) {
        return (
            <div className={dibsCss.mtMedium}>
                <div className={variantStyles.infoGroupContainer}>
                    <div>
                        <PriceDisplay
                            sku={null}
                            useSvgIcon
                            item={item}
                            displayVatLabel
                            currency={currency}
                            isAuctionSiderailRedesignVariantLabelOnly
                            roundPrice
                            handleRoundPriceTracking={handleRoundPriceTracking}
                        />
                    </div>
                    {!isMobile && (
                        <div
                            className={dibsCss.sassyFontBodySmallHeavy}
                            data-tn="pdp-bidding-closed"
                        >
                            {copy.biddingClosed}
                        </div>
                    )}
                </div>
                {!isUnavailable && !isPausedAuction && !isUnavailableAuction && (
                    <div className={variantStyles.soldContainer}>
                        <div>
                            <PriceDisplay
                                sku={null}
                                useSvgIcon
                                item={item}
                                displayVatLabel
                                currency={currency}
                                isAuctionSiderailRedesignVariant
                                roundPrice
                                handleRoundPriceTracking={handleRoundPriceTracking}
                            />
                        </div>
                        {!!reserveMet && !isSecondChanceOfferSent && (
                            <div>
                                <PdpSharedAuctionViewHistory
                                    itemId={item.serviceId}
                                    currency={currency}
                                />
                            </div>
                        )}
                        <div>
                            <Button
                                className={dibsCss.mtXsmall}
                                size={isMobile ? 'large' : 'medium'}
                                type="transparent"
                                href="/auctions/"
                                fullWidth
                            >
                                <FormattedMessage
                                    id="PdpSharedAuctionEndedLink.browseCurrentAuctions"
                                    defaultMessage="Browse Current Auctions"
                                />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <span className={styles.closed} data-tn="pdp-bidding-closed">
                {copy.biddingClosed}
            </span>
            <span className={styles.price}>
                <PriceDisplay
                    currency={currency}
                    item={item}
                    useSvgIcon
                    roundPrice
                    handleRoundPriceTracking={handleRoundPriceTracking}
                />
            </span>
            {linkToBidHistory && (
                <PdpSharedAuctionViewHistory itemId={item.serviceId} currency={currency} />
            )}
        </div>
    );
};
